// ─────────────────────────────────────────────────────────────────────────────
//  THE SEAM.
//  Everything in src/app talks to compute ONLY through this interface.
//  Today: createLocalRunner()  (local Web Worker pool — see local-runner.ts)
//  Later: connect({ apiKey })  (mesh coordinator) implements the SAME interface,
//         and nothing in the UI, kernels, results, or permalinks changes.
//  Do not bypass this boundary. It is the entire reason a static tool can later
//  become a meshed product without a rewrite.
// ─────────────────────────────────────────────────────────────────────────────

export interface RunRequest {
  /** Gallery kernel id, or "custom" when running pasted source (M2). */
  kernelId: string;
  /** Total iterations. */
  n: number;
  /** Reproducibility seed (string). Same kernel + seed + n => identical result bits. */
  seed: string;
  /**
   * Custom kernel source (M2) — a self-contained ES module that default-exports a Kernel.
   * Present only when `kernelId === "custom"`. Transported to the worker and loaded as a
   * module via a Blob URL under a strict CSP (no eval/new Function); the determinism linter
   * (src/sdk/lint.ts) must pass before this is ever dispatched.
   */
  source?: string;
  /**
   * Tunable parameter values (key → number) for kernels that declare a `params` schema.
   * Part of the deterministic inputs: same (kernel, seed, n, params) ⇒ identical result
   * bits. Missing/extra keys are resolved against the kernel's schema before compute, so a
   * partial map is fine.
   */
  params?: Record<string, number>;
}

export interface RunProgress {
  completed: number;
  total: number;
}

/**
 * A single chunk landing, streamed live as the run progresses. This is the data feed the
 * app's live visuals consume (running-estimate convergence + the per-worker "materialise"
 * grid). It is **advisory and order-independent**: chunks may arrive in any completion order
 * and consuming them never affects `resultBits`, which is always assembled by `assembleResult`
 * over partials in fixed chunk-index order. A live "running estimate" built from these is a
 * preview, not the canonical result.
 */
export interface ChunkEvent {
  /** Logical chunk id (stable, ordered) — the cell index in the materialise grid. */
  chunkIndex: number;
  /** Total number of logical chunks in this run (a fixed function of (kernel, N)) — lets a
   *  consumer size the materialise grid up front without reaching past the seam. */
  numChunks: number;
  /** Iterations in this chunk. */
  count: number;
  /** This chunk's scalar estimate (the histogram datum / convergence sample). */
  sample: number;
  /** Which pool worker / mesh peer returned it — drives the per-worker colouring. */
  workerId: number;
  /** Running iteration count (same value as RunProgress.completed). */
  completed: number;
  total: number;
}

/** Live hooks for a run. All optional and advisory; none affect the result. */
export interface RunHooks {
  onProgress?(p: RunProgress): void;
  onChunk?(c: ChunkEvent): void;
}

export interface RunResult {
  result: number;
  /** IEEE-754 hex of `result` — the reproducible result hash (Spike 02 `_bits`). */
  resultBits: string;
  /** Sample series for the histogram (one value per chunk in M0). */
  samples: number[];
  kernelHash: string;
  /** Which kernel produced this — so the UI can label/title the result. */
  kernelId: string;
  /** Human label for the headline number (e.g. "call price"), from the kernel. */
  resultUnit: string;
  /** Whether the kernel is bitwise-verifiable on this engine (drives the result tier badge). */
  verifiable: boolean;
  seed: string;
  n: number;
  elapsedMs: number;
  /** The resolved parameter values the run actually used (defaults applied + clamped). */
  params: Record<string, number>;
  /** Reproducibility version triple (sdk/shim/fdlibm) the run was produced under — RFC-0008 §10. */
  triple: import("./version").VersionTriple;
}

export interface Runner {
  /**
   * Run a job to completion. The second argument accepts either the legacy `onProgress`
   * function (back-compatible) or a `RunHooks` object carrying `onProgress` and the new
   * `onChunk` stream. All hooks are advisory and never affect the result.
   */
  run(
    req: RunRequest,
    hooks?: RunHooks | ((p: RunProgress) => void),
  ): Promise<RunResult>;
  /** Release resources (terminate workers / close sockets). */
  dispose(): void;
}

/** Normalise the back-compatible `run()` second argument into a `RunHooks` object. */
export function normalizeHooks(
  hooks: RunHooks | ((p: RunProgress) => void) | undefined,
): RunHooks {
  if (typeof hooks === "function") return { onProgress: hooks };
  return hooks ?? {};
}
